import { initializeModal } from "../../Shared/Modal/modal";
import {
  getRequiredDatasetAttribute,
  getRequiredElement
} from "../../Shared/Utilities/attributes";
import {
  acceptTargetingConsent,
  hasTargetingConsent
} from "../../Shared/Utilities/cookieConsent";
import { prefersReducedMotion } from "../../Shared/Utilities/userPreferences";
import {
  getPlayer,
  isVimeoPlayer,
  isYouTubeUrl,
  setVideoDurationStructuredData
} from "../../Shared/Utilities/video";
import { pauseVideo, playVideo } from "../../Shared/Video/video";

const largeTeasers = document.querySelectorAll<HTMLElement>(
  "[data-large-teaser-video='true']"
);

const getPlayPauseElements = (container: HTMLElement) => {
  const playPauseButton = container.querySelector<HTMLElement>(
    "[data-js-play-pause-button]"
  );

  if (!playPauseButton) {
    return { playPauseButton: null, playButton: null, pauseButton: null };
  }

  return {
    playPauseButton,
    playButton: playPauseButton.querySelector<HTMLElement>(
      "[data-js-symbol-name='play']"
    ),
    pauseButton: playPauseButton.querySelector<HTMLElement>(
      "[data-js-symbol-name='pause']"
    )
  };
};

largeTeasers.forEach(async largeTeaser => {
  let isPlaying = false;

  const hasModal = getRequiredDatasetAttribute(
    largeTeaser,
    "hasModal",
    "Modal indicator not found."
  );

  if (hasModal === "true") {
    const modal = getRequiredElement<HTMLElement>(
      largeTeaser,
      "[data-modal]",
      "Modal not found"
    );

    initializeModal(modal);

    const modalIframe = getRequiredElement<HTMLIFrameElement>(
      modal,
      "iframe",
      "Iframe not found in modal"
    );

    const isYouTube = isYouTubeUrl(modalIframe.src);
    let modalPlayer: ReturnType<typeof getPlayer>;

    const consentWarning = modal.querySelector<HTMLElement>("[data-consent-warning]");

    if (!isYouTube) {
      modalPlayer = getPlayer(modalIframe);

      if (modalPlayer) {
        await setVideoDurationStructuredData(modalPlayer);
      }
    }

    const initYouTubeAndPlay = async () => {
      if (consentWarning) {
        consentWarning.inert = true;
      }

      if (!modalPlayer) {
        modalPlayer = getPlayer(modalIframe);

        if (modalPlayer) {
          setVideoDurationStructuredData(modalPlayer).catch(console.warn);
        }
      }
      if (modalPlayer) {
        await playVideo(modalPlayer);
      }
    };

    modal.addEventListener("modal:open", async () => {
      if (isYouTube) {
        if (hasTargetingConsent()) {
          await initYouTubeAndPlay();
        } else if (consentWarning) {
          consentWarning.inert = false;
        }
      } else if (modalPlayer) {
        await playVideo(modalPlayer);
      }
    });

    modal.addEventListener("modal:close", async () => {
      if (consentWarning) {
        consentWarning.inert = true;
      }

      /**
         * Pause YouTube videos when the modal closes.
         *
         * We send a postMessage command directly to the iframe instead of using a
         * YT.Player instance. Once cookies are accepted, Google Tag Manager's YouTube
         * trigger attaches its own YT.Player to the same iframe (currently from two
         * containers, see the data-gtm-yt-inspected-* attributes) and may reload it.
         * That breaks any player reference we hold, so stopVideo() silently did nothing
         * and the video kept playing after close.
         *
         * This requires enablejsapi=1 in the embed URL. The iframe is queried at close
         * time because GTM may have replaced the element since page load.
      */
      modal.querySelectorAll<HTMLIFrameElement>("iframe[src*='youtube']").forEach((iframe) => {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
          "*"
        );
      });


      if (modalPlayer) {
        await pauseVideo(modalPlayer);
      }
    });

    modal
      .querySelector<HTMLElement>("[data-accept-targeted-cookies]")
      ?.addEventListener("click", async () => {
        acceptTargetingConsent();
        await initYouTubeAndPlay();
      });

    modal
      .querySelector<HTMLElement>("[data-close-consent-warning]")
      ?.addEventListener("click", () => {
        window.ani_modals?.get(modal.id)?.close();
      });
  }

  const teaserVideoIframe =
    largeTeaser.querySelector<HTMLIFrameElement>("[data-teaser-video]");

  if (teaserVideoIframe) {
    const teaserVideoPlayer = getPlayer(teaserVideoIframe);

    if (!teaserVideoPlayer) {
      console.error("Couldn't get a video player");
      return;
    }

    if (!isVimeoPlayer(teaserVideoPlayer)) {
      console.error("Background videos only support Vimeo");
      return;
    }

    teaserVideoPlayer.on("play", () => (teaserVideoIframe.ariaHidden = "false"));

    if (hasModal === "false") {
      const { playPauseButton, playButton, pauseButton } =
        getPlayPauseElements(largeTeaser);

      if (playPauseButton && playButton && pauseButton) {
        playPauseButton.addEventListener("click", () => {
          playButton.classList.toggle("hidden");
          pauseButton.classList.toggle("hidden");
          isPlaying ? pauseVideo(teaserVideoPlayer) : playVideo(teaserVideoPlayer);
          isPlaying = !isPlaying;
        });
      }
    }

    if (!prefersReducedMotion() || hasModal === "false") {
      const scrolledIntoViewHandler = (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            playVideo(teaserVideoPlayer);
            const { playPauseButton, playButton, pauseButton } = getPlayPauseElements(
              entry.target as HTMLElement
            );
            if (playPauseButton && playButton && pauseButton) {
              playButton.classList.toggle("hidden");
              pauseButton.classList.toggle("hidden");
              isPlaying = true;
            }
            intersectionObserver.unobserve(entry.target);
          }
        });
      };

      const intersectionObserver = new IntersectionObserver(scrolledIntoViewHandler, {
        threshold: 0
      });
      intersectionObserver.observe(largeTeaser);
    }
  }
});
